/**
 * Intercepts inter-page markdown links and turns them into in-app navigation.
 *
 * For any `<a href="...">` whose href looks like a `.md` link, this enricher:
 *   - Resolves the href against the current file's directory + project file list
 *   - Replaces the default browser navigation with a `loadFile()` call
 *   - Adds a `.leedoc-broken-link` class to links that don't match any page
 *   - Optionally invokes an `onBrokenLink` callback so the host app can offer
 *     a "create page here?" affordance
 *
 * External URLs (http://, https://, mailto:, etc.) and pure anchor links are
 * left alone.
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';
import { resolveLink, type ResolvedLink } from '../../shared/link-resolver.js';

export interface LinkNavigationConfig extends EnricherConfig {
  /** Called when a resolved link is clicked. Receives the page path (with `.src.md`). */
  onNavigate: (path: string, opts: { newTab: boolean; anchor: string | null }) => void;
  /** Called when a broken link is clicked. Receives the unresolved href. */
  onBrokenLink?: (href: string) => void;
  /** Returns the current set of page paths (with `.src.md` extension). */
  getAllPaths: () => readonly string[];
  /** Returns the path of the file the preview was rendered from. */
  getCurrentFile: () => string | null;
}

const EXTERNAL_PREFIXES = ['http://', 'https://', 'mailto:', 'tel:', 'ftp://', 'data:', 'javascript:', 'media:'];

export class LinkNavigationEnricher extends BaseEnricher {
  private readonly opts: LinkNavigationConfig;

  constructor(opts: LinkNavigationConfig) {
    super({ priority: opts.priority ?? 92 });
    this.opts = opts;
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    const links = container.querySelectorAll<HTMLAnchorElement>('a[href]');
    const currentFile = this.opts.getCurrentFile();
    const allPaths = this.opts.getAllPaths();

    links.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) return;
      if (isExternal(href)) return;
      if (href.startsWith('#')) return; // in-page anchor — leave to TOC behaviour

      const hasMdExtension = looksLikePageLink(href);
      const resolved = resolveLink(href, currentFile, allPaths);

      if (resolved) {
        // Either explicit `.md`/`.src.md` extension, or a bare-name link that
        // matched an existing page. Treat as internal navigation.
        this.attachNavHandler(link, resolved);
      } else if (hasMdExtension) {
        // Author clearly intended a page link (used `.md`) but the target is
        // missing — surface as a broken link with the create-page affordance.
        this.markBroken(link, href);
      }
      // Otherwise: bare-name link that doesn't match any page — leave as a
      // regular browser link (could be an arbitrary URL/path the author wants).
    });
  }

  private attachNavHandler(link: HTMLAnchorElement, resolved: ResolvedLink): void {
    link.classList.add('leedoc-page-link');
    link.dataset.leedocTarget = resolved.path;
    if (resolved.anchor) link.dataset.leedocAnchor = resolved.anchor;

    link.addEventListener('click', (e) => {
      // Let users middle-click / cmd-click for a true new tab (browser tab),
      // but for a normal left click we route through the app's tab system.
      if (e.button !== 0) return;
      const newTab = e.ctrlKey || e.metaKey;
      e.preventDefault();
      this.opts.onNavigate(resolved.path, { newTab, anchor: resolved.anchor });
    });
  }

  private markBroken(link: HTMLAnchorElement, href: string): void {
    link.classList.add('leedoc-broken-link');
    link.title = `No page found at "${href}" — click to create`;

    link.addEventListener('click', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      if (this.opts.onBrokenLink) {
        this.opts.onBrokenLink(href);
      }
    });
  }
}

function isExternal(href: string): boolean {
  const lower = href.toLowerCase();
  return EXTERNAL_PREFIXES.some(p => lower.startsWith(p));
}

/**
 * A link is considered a page link if it ends in `.md` / `.src.md`, or carries
 * one of those extensions before a `#anchor`. Bare-name links (no extension)
 * are NOT auto-treated as page links because they collide with normal anchor
 * targets and arbitrary file references.
 */
function looksLikePageLink(href: string): boolean {
  const noAnchor = href.split('#')[0];
  return /\.(src\.md|md)$/i.test(noAnchor);
}
